import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import NpdQuoteSheet from "@/components/NpdQuoteSheet";

/**
 * Read-only quote preview of a SAVED round (N1, N2, …). Renders the round's frozen
 * snapshot through the same NpdQuoteSheet used for the live preview. The drawing
 * screenshot shown is the NPD's current one (snapshots don't store a separate image).
 */
export default function NpdRoundPreview() {
  const { id, roundId } = useParams();
  const [round, setRound] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({});
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [rr, c, s, u] = await Promise.all([
          api.get(`/npd/${id}/rounds`),
          api.get("/customers"),
          api.get("/settings/public"),
          api.get("/units"),
        ]);
        const match = (rr.data?.rounds || []).find((r) => r.round_id === roundId);
        if (!match) throw new Error("Round not found");
        setRound(match);
        setCustomers(c.data || []);
        setSettings(s.data || {});
        setUnits(u.data || []);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Failed to load round";
        setLoadError(msg);
        toast.error(msg);
      } finally { setLoading(false); }
    })();
  }, [id, roundId]);

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (loadError) return (
    <div className="p-10 text-center">
      <div className="text-red-600 font-medium mb-2">Could not load round preview</div>
      <div className="text-sm text-slate-500">{loadError}</div>
      <Link to={`/npd/${id}`} className="inline-block mt-4 text-sm text-orange-600 hover:text-orange-700">
        ← Back to NPD
      </Link>
    </div>
  );

  const npd = round?.snapshot || {};
  const customerName = customers.find((c) => c.customer_id === npd.customer_id)?.name || "—";
  const unit = units.find((u) => u.unit_id === npd.unit_id) || null;
  const dateStr = round?.round_date ? new Date(round.round_date).toLocaleString("en-IN") : "";

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between print:hidden">
        <Link to={`/npd/${id}`} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5">
          <ArrowLeft size={16} /> Back to NPD
        </Link>
        <div className="text-sm text-slate-500">
          Round <span className="font-semibold text-sky-700">{round?.round_label}</span> · {npd.npd_number}
          {dateStr && <span className="text-slate-400"> · saved {dateStr}</span>}
        </div>
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md flex items-center gap-1.5"
          data-testid="print-btn"
        >
          <Printer size={14} /> Print / Save as PDF
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white shadow-md p-6 my-6 print:shadow-none print:my-0 print:p-0">
        <div className="mb-3 text-[11px] text-slate-500 border border-slate-200 bg-slate-50 rounded px-3 py-2 print:hidden">
          Read-only snapshot as saved for <strong>{round?.round_label}</strong>. Later edits to the live NPD do not change this view.
        </div>
        <NpdQuoteSheet npd={npd} customerName={customerName} settings={settings} unit={unit} />
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 12mm; }
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
          .quote-sheet { font-size: 11px; }
        }
      `}</style>
    </div>
  );
}
